
import { useState, useEffect } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useToastAction } from "@/hooks/useToastAction";
import { Crown } from "lucide-react";

interface Feature {
  key: string;
  limit: number;
  name: string;
  resetInterval: 'daily' | 'weekly' | 'monthly' | 'none';
}

const FEATURES = {
  PRAYER_REQUEST: { 
    key: "prayer_request", 
    limit: 1, 
    name: "Prayer Request",
    resetInterval: 'weekly'
  },
  JOURNAL_ENTRY: { 
    key: "journal_entry", 
    limit: 1, 
    name: "Journal Entry",
    resetInterval: 'weekly'
  },
  DAILY_DEVOTIONAL: { 
    key: "daily_devotional", 
    limit: 1, 
    name: "Daily Devotional",
    resetInterval: 'weekly'
  },
};

export function useFeatureLimit() {
  const session = useSession();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { createToastAction } = useToastAction();
  const [usageCounts, setUsageCounts] = useState<Record<string, number>>({});
  const [lastResetTimes, setLastResetTimes] = useState<Record<string, string>>({});

  // Load usage counts and reset times from localStorage on mount
  useEffect(() => {
    if (!session) {
      try {
        const savedUsage = localStorage.getItem('feature_usage');
        const savedResetTimes = localStorage.getItem('feature_reset_times');
        if (savedUsage) {
          setUsageCounts(JSON.parse(savedUsage));
        }
        if (savedResetTimes) {
          setLastResetTimes(JSON.parse(savedResetTimes));
        }
      } catch (error) {
        console.error("Error loading feature usage data:", error);
        // Reset if there's an error
        localStorage.removeItem('feature_usage');
        localStorage.removeItem('feature_reset_times');
      }
    }
  }, [session]);

  // Save usage counts and reset times to localStorage whenever they change
  useEffect(() => {
    if (!session && Object.keys(usageCounts).length > 0) {
      localStorage.setItem('feature_usage', JSON.stringify(usageCounts));
    }
    if (!session && Object.keys(lastResetTimes).length > 0) {
      localStorage.setItem('feature_reset_times', JSON.stringify(lastResetTimes));
    }
  }, [usageCounts, lastResetTimes, session]);

  // Check if a feature needs to be reset based on its interval
  const checkReset = (featureKey: string) => {
    const feature = Object.values(FEATURES).find(f => f.key === featureKey);
    if (!feature || feature.resetInterval === 'none') return false;
    
    const lastResetTime = lastResetTimes[featureKey];
    if (!lastResetTime) return true; // First time use, no reset needed but set reset time
    
    const now = new Date();
    const lastReset = new Date(lastResetTime);
    
    switch (feature.resetInterval) {
      case 'daily':
        // Reset if it's a different day
        return now.toDateString() !== lastReset.toDateString();
      case 'weekly':
        // Reset if it's been more than 7 days
        const oneWeek = 7 * 24 * 60 * 60 * 1000;
        return now.getTime() - lastReset.getTime() >= oneWeek;
      case 'monthly':
        // Reset if it's a different month
        return now.getMonth() !== lastReset.getMonth() || 
               now.getFullYear() !== lastReset.getFullYear();
      default:
        return false;
    }
  };

  const incrementUsage = (featureKey: string): boolean => {
    if (session) return true; // Logged in users have unlimited access
    
    const currentCount = usageCounts[featureKey] || 0;
    const feature = Object.values(FEATURES).find(f => f.key === featureKey);
    
    if (!feature) return true; // If feature not found in limits, allow it
    
    // Check if we need to reset the counter for this feature
    if (checkReset(featureKey)) {
      // Reset the counter
      const newUsageCounts = {...usageCounts};
      newUsageCounts[featureKey] = 0;
      setUsageCounts(newUsageCounts);
      
      // Update the reset time
      const newResetTimes = {...lastResetTimes};
      newResetTimes[featureKey] = new Date().toISOString();
      setLastResetTimes(newResetTimes);
      
      // Allow usage
      setUsageCounts(prev => ({
        ...prev,
        [featureKey]: 1
      }));
      return true;
    }
    
    if (currentCount >= feature.limit) {
      toast({
        title: "Feature Limit Reached",
        description: `You've reached the weekly free limit for ${feature.name}. Sign up for unlimited access.`,
        action: createToastAction({
          label: "Sign Up",
          onClick: () => navigate("/premium"),
          icon: <Crown className="w-4 h-4" />,
        }),
        duration: 6000,
      });
      return false;
    }
    
    // Increment the usage count
    setUsageCounts(prev => ({
      ...prev,
      [featureKey]: currentCount + 1
    }));
    
    // If this is the first use, set the reset time
    if (currentCount === 0 && !lastResetTimes[featureKey]) {
      setLastResetTimes(prev => ({
        ...prev,
        [featureKey]: new Date().toISOString()
      }));
    }
    
    return true;
  };

  const checkRemainingUses = (featureKey: string): number => {
    if (session) return Infinity; // Logged in users have unlimited access
    
    const feature = Object.values(FEATURES).find(f => f.key === featureKey);
    if (!feature) return Infinity;
    
    // Check if we need to reset the counter
    if (checkReset(featureKey)) {
      return feature.limit; // Full limit available after reset
    }
    
    const currentCount = usageCounts[featureKey] || 0;
    return Math.max(0, feature.limit - currentCount);
  };

  return {
    incrementUsage,
    checkRemainingUses,
    features: FEATURES,
  };
}
